import {
    AccountInfo,
    AuthError,
    IPublicClientApplication,
    InteractionRequiredAuthError,
} from "@azure/msal-browser"
import { loginRequest, silentRedirectUri } from "../authConfig"

export const INTERACTIVE_LOGIN_REQUIRED = "INTERACTIVE_LOGIN_REQUIRED"

export interface InteractiveLoginRequiredError extends Error {
    code: typeof INTERACTIVE_LOGIN_REQUIRED
    cause?: unknown
}

// Silent-iframe failures that are recoverable by falling back to an interactive
// login. These all mean "the silent flow can't complete; ask the user".
const RECOVERABLE_SILENT_ERROR_CODES = new Set([
    "block_iframe_reload",
    "monitor_window_timeout",
    "iframe_closed_prematurely",
    "silent_sso_error",
    "silent_prompt_value_error",
    "no_token_request_cache_error",
    "timed_out",
])

const isRecoverableSilentError = (error: unknown): boolean => {
    if (error instanceof InteractionRequiredAuthError) return true
    if (error instanceof AuthError && RECOVERABLE_SILENT_ERROR_CODES.has(error.errorCode)) return true
    return false
}

const makeInteractiveLoginRequired = (cause: unknown): InteractiveLoginRequiredError => {
    const err = new Error("Interactive login required") as InteractiveLoginRequiredError
    err.code = INTERACTIVE_LOGIN_REQUIRED
    err.cause = cause
    return err
}

// Coalesce concurrent silent acquisitions for the same account so a token-refresh
// storm (every dashboard widget hitting useAxiosPrivate at once) doesn't open
// multiple popups when silent fails.
const inflight = new Map<string, Promise<string>>()

const doAcquire = async (
    instance: IPublicClientApplication,
    account: AccountInfo,
): Promise<string> => {
    try {
        const response = await instance.acquireTokenSilent({
            ...loginRequest,
            account,
            redirectUri: silentRedirectUri,
        })
        return response.accessToken
    } catch (silentError) {
        if (!isRecoverableSilentError(silentError)) {
            throw silentError
        }
        // Match LoginView's InteractionType.Redirect flow. Popup caused a
        // cold-start account-picker popup and a no_token_request_cache_error
        // race when it completed before the main window's MSAL state was ready.
        await instance.acquireTokenRedirect({
            ...loginRequest,
            account,
        })
        // acquireTokenRedirect navigates the page away; this throw only fires
        // if navigation was blocked (e.g. another redirect already in progress).
        throw makeInteractiveLoginRequired(silentError)
    }
}

export const acquireToken = (
    instance: IPublicClientApplication,
    account: AccountInfo,
): Promise<string> => {
    const key = account.homeAccountId
    const existing = inflight.get(key)
    if (existing) return existing
    const promise = doAcquire(instance, account).finally(() => {
        inflight.delete(key)
    })
    inflight.set(key, promise)
    return promise
}
